/**
 * Text Notes tab of the Delivery Config page. Edits the `text_notes` section of
 * the per-facility delivery-steps document: pre-conditions, run conditions,
 * routing, and content (body columns / summary). Saves back via a partial
 * Firestore write that preserves the rest of the section (including document
 * name / status source field / eligible PA statuses, which are no longer edited
 * here).
 */
import { useEffect, useMemo, useState } from "react";
import { Button, openModal } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import QueryBuilder from "../../../../../components/queryBuilder/QueryBuilder";
import {
  CmmInputUniqueValuesProvider,
  useCmmInputUniqueValuesContext,
} from "../../../../../context/cmmInputUniqueValuesContext";
import { buildDeliveryStepConditionConfig } from "../shared/deliveryStepConditionConfig";
import StepSettingsModal from "../shared/stepSettingsModal";
import RoutingTable from "./routingTable";
import TextNotesContentSection from "./textNotesContentSection";
import { buildWhatWeAddOptions } from "./textNotesWhatWeAddOptions";
import { useTextNotesConfig } from "./useTextNotesConfig";

const TEXT_NOTES_SETTINGS_MODAL_ID = "text-notes-settings-modal";

/**
 * Section wrapper for Pre-Conditions / Conditions QueryBuilders.
 *
 * @param title - Section heading shown above the builder
 * @param children - QueryBuilder (or other condition UI)
 */
const ConditionsCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <div className="text-sm font-semibold text-primaryGray-1">{title}</div>
    {children}
  </div>
);

const TextNotesConfigInner = () => {
  const {
    form,
    loading,
    saving,
    error,
    isDirty,
    applySettingsForm,
    setContent,
    setPreconditions,
    setConditions,
    removeBodyColumn,
    addRoutingRow,
    updateRoutingRow,
    handleDiscard,
    handleSave,
  } = useTextNotesConfig();

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const conditionConfig = useMemo(
    () => buildDeliveryStepConditionConfig(cmmInputUniqueValues),
    [cmmInputUniqueValues],
  );

  const whatWeAddOptions = useMemo(
    () => buildWhatWeAddOptions(form.content.bodyColumns),
    [form.content.bodyColumns],
  );

  const [resetKey, setResetKey] = useState(0);
  const [settingsModalKey, setSettingsModalKey] = useState(0);
  const [shouldOpenSettings, setShouldOpenSettings] = useState(false);

  useEffect(() => {
    if (!shouldOpenSettings) {
      return;
    }
    openModal(TEXT_NOTES_SETTINGS_MODAL_ID);
    setShouldOpenSettings(false);
  }, [shouldOpenSettings, settingsModalKey]);

  const onDiscard = () => {
    handleDiscard();
    setResetKey((k) => k + 1);
    setSettingsModalKey((k) => k + 1);
  };

  const openSettings = () => {
    setSettingsModalKey((k) => k + 1);
    setShouldOpenSettings(true);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
        <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
          <LoaderMessage message="Loading text notes configuration..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
      <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="flex flex-shrink-0 flex-row items-start justify-between gap-4 border-b border-primaryGray-16 pb-4">
          <div className="flex flex-col">
            <div className="text-h11 font-bold text-primaryGray-1">
              Text Notes
            </div>
            <div className="mt-1 text-small text-primaryGray-4">
              Configure the generated text-note document, its routing, and when
              this delivery step runs.
            </div>
          </div>
          <Button
            buttonType="secondary"
            size="medium"
            onClick={openSettings}
            disabled={Boolean(error)}
            children="Settings"
          />
        </div>

        {error ? (
          <p className="text-body1 text-red-600" role="alert">
            {error}
          </p>
        ) : (
          <div className="mb-1 mt-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6">
            <StepSettingsModal
              key={`settings-modal-${settingsModalKey}`}
              dialogId={TEXT_NOTES_SETTINGS_MODAL_ID}
              idPrefix="text-notes"
              title="Text Notes Settings"
              enabled={form.enabled}
              action={form.action}
              statusField={form.statusField}
              settings={form.settings}
              onApply={applySettingsForm}
            />

            <ConditionsCard title="Pre-Conditions">
              <QueryBuilder
                key={`pre-${resetKey}`}
                value={form.preconditions}
                onChange={setPreconditions}
                config={conditionConfig}
              />
            </ConditionsCard>

            <ConditionsCard title="Conditions">
              <QueryBuilder
                key={`cond-${resetKey}`}
                value={form.conditions}
                onChange={setConditions}
                config={conditionConfig}
              />
            </ConditionsCard>

            {/* Eligible PA Statuses table is intentionally not rendered; the
                stored `pa_status_title_options` still round-trip through the form. */}

            <RoutingTable
              resetKey={resetKey}
              rows={form.routingRows}
              whatWeAddOptions={whatWeAddOptions}
              onAdd={addRoutingRow}
              onChange={updateRoutingRow}
            />

            <TextNotesContentSection
              resetKey={resetKey}
              value={form.content}
              onChange={setContent}
              onRemoveBodyColumn={removeBodyColumn}
            />
          </div>
        )}

        <div className="flex flex-shrink-0 flex-row items-center justify-end gap-2 border-t border-primaryGray-16 pt-2">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={onDiscard}
            disabled={!isDirty || saving}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isDirty || saving}
            children={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>
    </div>
  );
};

/** Text Notes tab wrapped with the CMM value-search context the QueryBuilder needs. */
const TextNotesConfig = () => (
  <CmmInputUniqueValuesProvider>
    <TextNotesConfigInner />
  </CmmInputUniqueValuesProvider>
);

export default TextNotesConfig;
